import { useHomepagePrefetchOnHover } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useHomepagePrefetchOnHover";
import { Core } from "@faire/design-tokens";
import { Close } from "@faire/slate/icons/Close";
import home from "@faire/web-api--source/routes/www/root";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { Button } from "@faire/web/slate/Button";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Link } from "@faire/web/ui/LinkAnchor";
import { FaireLogo } from "@faire/web/ui/logos/FaireLogo";
import { RoutingContext } from "@faire/web/ui/routing/RoutingContext";
import { prependLocaleToUrl } from "@faire/web/ui/routing/util";
import React from "react";
import styled from "styled-components";

interface IProps {
  onBack?: () => void;
  onClose?: () => void;
  inverse?: boolean;
  backgroundColor?: string;
  forceDirectLoad?: boolean;
  showSkipCta?: boolean;
}

export const HeaderNavigation: React.FC<IProps> = ({
  onBack,
  onClose,
  inverse,
  backgroundColor,
  forceDirectLoad,
  showSkipCta,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const handlePrefetchHomepage = useHomepagePrefetchOnHover();

  return (
    <Container $inverse={inverse} $backgroundColor={backgroundColor}>
      {onBack ? (
        <IconBtn
          aria-label={strictLocalize({
            id: "9STaCU",
            defaultMessage: "Back",
            description: { text: "Button to navigate back" },
          })}
          onClick={onBack}
          shape="square"
          variant="tertiary"
          style={{ justifySelf: "flex-start" }}
          color={inverse ? Core.icon.inverse : Core.icon.default}
        >
          <Chevron direction="left" />
        </IconBtn>
      ) : (
        <span />
      )}
      <Column align="center">
        <Spacer height="2x" />
        {forceDirectLoad ? (
          <LogoDirectLink
            href={home()}
            onMouseEnter={handlePrefetchHomepage}
            onTouchStart={handlePrefetchHomepage}
          >
            <FaireLogo className="w-[88px] md:w-[118px]" inverse={inverse} />
          </LogoDirectLink>
        ) : (
          <LogoLink
            to={home()}
            onMouseEnter={handlePrefetchHomepage}
            onTouchStart={handlePrefetchHomepage}
          >
            <FaireLogo className="w-[88px] md:w-[118px]" inverse={inverse} />
          </LogoLink>
        )}
        <Spacer height="2x" />
      </Column>
      {onClose ? (
        <CloseButton
          onClose={onClose}
          inverse={inverse}
          showSkipCta={showSkipCta}
        />
      ) : null}
    </Container>
  );
};

const CloseButton: React.FC<{
  onClose: () => void;
  inverse?: boolean;
  showSkipCta?: boolean;
}> = ({ onClose, inverse, showSkipCta }) => {
  const { strictLocalize } = useStrictLocalization();

  if (showSkipCta) {
    return (
      <Button
        variant="tertiary"
        onClick={onClose}
        type="button"
        style={{
          justifySelf: "flex-end",
          paddingRight: getSpacing("3x"),
        }}
      >
        {strictLocalize({
          id: "4hCRG2",
          defaultMessage: "Skip",
          description: { text: "Button to skip the step" },
        })}
      </Button>
    );
  }
  return (
    <IconBtn
      aria-label={strictLocalize({
        id: "X1T3Oe",
        defaultMessage: "Close",
        description: { text: "Button to close a modal" },
      })}
      onClick={onClose}
      shape="square"
      variant="tertiary"
      style={{ justifySelf: "flex-end" }}
      color={inverse ? Core.icon.inverse : Core.icon.default}
    >
      <Close />
    </IconBtn>
  );
};

const DirectLoadLink: React.FC<{
  className?: string;
  href: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onTouchStart?: () => void;
}> = ({ className, href, onClick, children, onMouseEnter, onTouchStart }) => {
  const routingContext = React.useContext(RoutingContext);
  const hrefWithLocale = prependLocaleToUrl(href, routingContext);
  return (
    // Use <a> instead of <Link> to force hard reload.
    // eslint-disable-next-line @faire/no-raw-anchor-tag
    <a
      className={className}
      href={hrefWithLocale}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onTouchStart={onTouchStart}
    >
      {children}
    </a>
  );
};

const LogoDirectLink = styled(DirectLoadLink)`
  display: flex;
  flex-direction: column;

  justify-content: center;

  height: 24px;
  ${responsiveMediaQueries.tabletAndAbove} {
    height: 32px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  flex-direction: column;

  justify-content: center;

  height: 24px;
  ${responsiveMediaQueries.tabletAndAbove} {
    height: 32px;
  }
`;

const Container = styled.header<{
  $inverse?: boolean;
  $backgroundColor?: string;
}>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
  justify-content: center;

  width: 100%;

  background-color: ${({ $backgroundColor: backgroundColor }) =>
    backgroundColor ?? Core.surface.inverse};

  svg {
    color: ${({ $inverse }) =>
      $inverse ? Core.icon.inverse : Core.icon.default};
  }
`;

const IconBtn = styled(IconButton)<{ color: string }>`
  // TODO(tibawatanabe): IconButton has an issue with color inheritance, we can remove this code once it's fixed
  svg {
    color: ${({ color }) => color};
  }
`;
